import { useMemo } from 'react';
import { useParticipantEPR } from '../ParticipantEPRContext';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Divider,
  Chip,
  Stack,
  Skeleton,
} from '@mui/material';

const ACCENT = 'rgb(219, 234, 254)';    // #DBEAFE
const ACCENT_BORDER = '#60A5FA';        // blue-400
const PANEL_BG = 'white';

export default function Overview() {
  const { eprData, loading, reload } = useParticipantEPR();

  const p = eprData?.patient ?? {};
  const medicalhx = eprData?.medicalhx ?? [];
  const surgicalhx = eprData?.surghx ?? [];
  const allergies = eprData?.allergies ?? [];
  const alerts = eprData?.alerts ?? [];
  const scendetails = eprData?.scenarioDetails ?? {};

  // latest vitals if present
  const latestObs = useMemo(() => {
    const obs = [...(eprData?.eprObs ?? [])].sort(
      (a, b) => new Date(a.datetime) - new Date(b.datetime)
    );
    return obs[obs.length - 1];
  }, [eprData]);

  const demoRows = [
    { label: 'Name', value: p.name || '—' },
    { label: 'DOB', value: p.dob ? dayjs(p.dob).format('DD/MM/YYYY') : '—' },
    { label: 'Sex', value: p.sex || '—' },
    { label: 'Address', value: '28 West Drive, The Street, AB12 1CD' }, // as in your original
  ];

  const vitals = [
    { k: 'Temp', v: latestObs?.temp ? `${latestObs.temp}°C` : '—' },
    { k: 'Pulse', v: latestObs?.pulse ?? '—' },
    { k: 'RR', v: latestObs?.rr ?? '—' },
    { k: 'SpO₂', v: latestObs?.spo2 ? `${latestObs.spo2}%` : '—' },
    { k: 'SBP', v: latestObs?.sbp ?? '—' },
    { k: 'AVPU', v: latestObs?.avpu ?? '—' },
  ];

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
      <Stack spacing={2} sx={{ maxWidth: 1300, mx: 'auto' }}>
        {/* Vignette */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${ACCENT_BORDER}`,
            overflow: 'hidden',
            bgcolor: PANEL_BG,
          }}
        >
          <Box
            sx={{
              px: 2, py: 1.25,
              bgcolor: ACCENT,
              borderBottom: `1px solid ${ACCENT_BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Vignette</Typography>
            {loading ? (
              <Skeleton width={140} height={18} />
            ) : null}
          </Box>
          <Box sx={{ p: 2.5, bgcolor: '#F8FAFF' }}>
            {loading ? (
              <Stack spacing={1.2}>
                <Skeleton height={20} />
                <Skeleton height={20} />
                <Skeleton width="60%" height={20} />
              </Stack>
            ) : (
              <Typography sx={{ lineHeight: 1.7 }}>
                {scendetails?.vignette || '—'}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Row: Demographics | Medical & Surgical | Alerts & Allergies */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 1.2fr', gap: 2 }}>
          {/* Demographics + Latest Obs */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${ACCENT_BORDER}`,
              overflow: 'hidden',
              bgcolor: PANEL_BG,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Header title="Demographics" />
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Stack spacing={1.2}>
                  {demoRows.map((_, i) => <Skeleton key={i} height={18} />)}
                </Stack>
              ) : (
                <Stack spacing={1.25}>
                  {demoRows.map((r) => (
                    <Row key={r.label} label={r.label} value={r.value} />
                  ))}
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontWeight: 700, mb: 1 }}>Recent vitals</Typography>
              {loading ? (
                <Stack direction="row" spacing={1}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" width={70} height={32} />
                  ))}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {vitals.map(({ k, v }) => (
                    <Chip
                      key={k}
                      label={`${k}: ${v}`}
                      size="small"
                      sx={{ mb: 1, bgcolor: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>

          {/* Medical + Surgical History */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${ACCENT_BORDER}`,
              overflow: 'hidden',
              bgcolor: PANEL_BG,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Header title="History" right={
              <Chip
                size="small"
                sx={{ bgcolor: 'white', border: `1px solid ${ACCENT_BORDER}` }}
                label={`${medicalhx.length} medical • ${surgicalhx.length} surgical`}
              />
            }/>
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Medical</Typography>
              {loading ? (
                <Stack spacing={1}><Skeleton height={18} /><Skeleton height={18} /><Skeleton height={18} /></Stack>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {(medicalhx.length ? medicalhx : ['—']).map((item, i) => (
                    <ListItem key={i} sx={{ py: 0.25 }}>{item}</ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Surgical</Typography>
              {loading ? (
                <Stack spacing={1}><Skeleton height={18} /><Skeleton height={18} /></Stack>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {(surgicalhx.length ? surgicalhx : []).map((item, i) => (
                    <ListItem key={i} sx={{ py: 0.25 }}>
                      {item.surgery} — {item.date ? dayjs(item.date).format('DD/MM/YYYY') : '—'}
                    </ListItem>
                  ))}
                  {surgicalhx.length === 0 && <ListItem sx={{ py: 0.25 }}>—</ListItem>}
                </List>
              )}
            </Box>
          </Paper>

          {/* Alerts + Allergies */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${ACCENT_BORDER}`,
              overflow: 'hidden',
              bgcolor: PANEL_BG,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Header title="Alerts & Allergies" />
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Alerts</Typography>
              {loading ? (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Skeleton variant="rounded" width={100} height={28} />
                  <Skeleton variant="rounded" width={120} height={28} />
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(alerts.length ? alerts : ['—']).map((a, i) => (
                    <Chip
                      key={i}
                      label={a}
                      size="small"
                      sx={{ mb: 1, bgcolor: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}
                    />
                  ))}
                </Stack>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Allergies</Typography>
              {loading ? (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Skeleton variant="rounded" width={100} height={28} />
                  <Skeleton variant="rounded" width={80} height={28} />
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(allergies.length ? allergies : ['—']).map((al, i) => (
                    <Chip
                      key={i}
                      label={al}
                      size="small"
                      sx={{ mb: 1, bgcolor: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}

/* ---------- small subcomponents for consistency ---------- */

function Header({ title, right }) {
  return (
    <Box
      sx={{
        px: 2, py: 1.25,
        bgcolor: ACCENT,
        borderBottom: `1px solid ${ACCENT_BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      {right ?? null}
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
      <Typography sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}
